import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Save, Loader2 } from 'lucide-react';
import Logo from '../components/Logo';
import Footer from '../components/Footer';
import FeedbackButton from '../components/FeedbackButton';

const profileAPI = {
  getProfile: () => {
    const token = localStorage.getItem('token');
    return fetch(`${process.env.REACT_APP_API_URL || 'https://teacher-material-backend-production.up.railway.app/api'}/profile`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(res => res.json());
  },
  updateProfile: (data) => {
    const token = localStorage.getItem('token');
    return fetch(`${process.env.REACT_APP_API_URL || 'https://teacher-material-backend-production.up.railway.app/api'}/profile`, {
      method: 'PATCH',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    }).then(res => res.json());
  }
};

export default function Profile() {
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    default_grade_level: '',
    default_subject: '',
    school_name: '',
    teaching_style: '',
    favorite_material_types: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await profileAPI.getProfile();
      setProfile(response.profile);
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      await profileAPI.updateProfile(profile);
      setMessage('Profile updated successfully! ✅');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Failed to update profile ❌');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #e8f5e9 0%, #e3f2fd 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ fontSize: '18px', color: '#2e7d32' }}>Loading profile...</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #e8f5e9 0%, #e3f2fd 100%)'
    }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #e0e0e0',
        padding: '20px 32px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              background: 'white',
              border: '2px solid #e0e0e0',
              borderRadius: '8px',
              color: '#424242',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            <ArrowLeft size={18} />
            Back
          </button>
          <Logo size="medium" showTagline={false} />
        </div>
      </div>

      {/* Tagline Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
        padding: '16px',
        textAlign: 'center',
        borderBottom: '3px solid #81c784'
      }}>
        <p style={{
          color: 'white',
          fontSize: '16px',
          fontWeight: '600',
          margin: 0,
          letterSpacing: '0.5px'
        }}>
          ✨ Built for Teachers, by a Teacher ✨
        </p>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '32px' }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '32px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <User size={32} color="#2e7d32" />
            <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#2e7d32', margin: 0 }}>
              My Profile
            </h1>
          </div>

          <p style={{ color: '#666', marginBottom: '32px' }}>
            Set your default preferences to speed up material generation. These will auto-fill the generator, but you can always change them per-material.
          </p>

          {message && (
            <div style={{
              padding: '12px 16px',
              background: message.includes('✅') ? '#e8f5e9' : '#ffebee',
              border: `1px solid ${message.includes('✅') ? '#66bb6a' : '#ef5350'}`,
              borderRadius: '8px',
              color: message.includes('✅') ? '#2e7d32' : '#c62828',
              marginBottom: '24px',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Name */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#424242',
                marginBottom: '8px'
              }}>
                Full Name
              </label>
              <input
                type="text"
                name="name"
                value={profile.name || ''}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#81c784'}
                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
              />
            </div>

            {/* Email (read-only) */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#424242',
                marginBottom: '8px'
              }}>
                Email Address
              </label>
              <input
                type="email"
                value={profile.email || ''}
                disabled
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '16px',
                  background: '#f5f5f5',
                  color: '#999',
                  boxSizing: 'border-box'
                }}
              />
              <p style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                Email cannot be changed
              </p>
            </div>

            {/* Default Grade Level */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#424242',
                marginBottom: '8px'
              }}>
                Default Grade Level
              </label>
              <input
                type="text"
                name="default_grade_level"
                value={profile.default_grade_level || ''}
                onChange={handleChange}
                placeholder="e.g., 9th Grade, High School"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#81c784'}
                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
              />
            </div>

            {/* Default Subject */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#424242',
                marginBottom: '8px'
              }}>
                Default Subject
              </label>
              <input
                type="text"
                name="default_subject"
                value={profile.default_subject || ''}
                onChange={handleChange}
                placeholder="e.g., Math, English, Science"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#81c784'}
                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
              />
            </div>

            {/* School Name */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#424242',
                marginBottom: '8px'
              }}>
                School Name (Optional)
              </label>
              <input
                type="text"
                name="school_name"
                value={profile.school_name || ''}
                onChange={handleChange}
                placeholder="e.g., Lincoln High School"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#81c784'}
                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
              />
            </div>

            {/* Save Button */}
            <button
              type="submit"
              disabled={saving}
              style={{
                width: '100%',
                padding: '14px',
                background: saving ? '#bdbdbd' : 'linear-gradient(135deg, #66bb6a 0%, #42a5f5 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: saving ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'transform 0.2s',
                boxShadow: '0 4px 12px rgba(102, 187, 106, 0.3)'
              }}
              onMouseOver={(e) => !saving && (e.target.style.transform = 'translateY(-2px)')}
              onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
            >
              {saving ? (
                <>
                  <Loader2 size={20} />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={20} />
                  Save Profile
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      <FeedbackButton />
      <Footer />
    </div>
  );
}